import { LayoutGrid, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState } from '@/components/feedback/States'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Can } from '@/features/auth/guards'
import { useAuth } from '@/features/auth/useAuth'
import { useDeleteSection } from '../api'
import { isWholeClassSection } from '../sectionLabel'
import type { SchoolClass, Section } from '../types'
import { DeleteDialog } from './DeleteDialog'
import { Occupancy } from './OccupancyBar'
import { SectionDialog } from './SectionDialog'

function nextSectionName(sections: Section[]): string {
  const used = new Set(sections.map((s) => s.name.toUpperCase()))
  for (let i = 0; i < 26; i++) {
    const letter = String.fromCharCode(65 + i)
    if (!used.has(letter)) return letter
  }
  return ''
}

export function SectionsPanel({ schoolClass }: { schoolClass: SchoolClass }) {
  const { t } = useTranslation('academic')
  const { can } = useAuth()
  const canManage = can('academic.manage')
  const remove = useDeleteSection()
  const [editing, setEditing] = useState<Section | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Section | null>(null)
  const editedId = editing === 'new' ? null : editing?.id
  const otherSections = schoolClass.sections.filter((s) => s.id !== editedId)
  // An unnamed section is the whole class as one group: it must stay the class's only section.
  const wholeClass = schoolClass.sections.some((s) => isWholeClassSection(s.name))
  /** Name to put in "section {{name}}" phrases; the class name stands in for an unnamed section. */
  const nameOf = (s: Section) => s.name || schoolClass.name

  const actions = (s: Section) => (
    <div className="flex justify-end gap-1">
      <IconButton
        label={t('Edit section {{name}}', { name: nameOf(s) })}
        icon={<Pencil />}
        size="icon-sm"
        onClick={() => setEditing(s)}
      />
      <IconButton
        label={t('Delete section {{name}}', { name: nameOf(s) })}
        icon={<Trash2 />}
        size="icon-sm"
        className="hover:text-danger-600"
        onClick={() => setDeleting(s)}
      />
    </div>
  )

  const columns: Column<Section>[] = [
    {
      key: 'name',
      header: t('Section'),
      cell: (s) => (
        <span className="font-medium text-ink">
          {s.name ? t('Section {{name}}', { name: s.name }) : t('Whole class')}
        </span>
      ),
    },
    {
      key: 'teacher',
      header: t('Class teacher'),
      cell: (s) =>
        s.classTeacher ? (
          <span className="flex items-center gap-2">
            <Avatar name={s.classTeacher.name} size="xs" />
            <span className="truncate">{s.classTeacher.name}</span>
          </span>
        ) : (
          <span className="text-subtle">{t('Not assigned')}</span>
        ),
    },
    {
      key: 'shift',
      header: t('Shift'),
      hideBelow: 'lg',
      cell: (s) =>
        s.shift ? <Badge size="sm">{s.shift.name}</Badge> : <span className="text-subtle">—</span>,
    },
    {
      key: 'room',
      header: t('Room'),
      hideBelow: 'lg',
      cell: (s) => s.room ?? <span className="text-subtle">—</span>,
    },
    {
      key: 'occupancy',
      header: t('Students'),
      cell: (s) => (
        <Occupancy
          count={s.studentCount}
          capacity={s.capacity}
          label={t('Section {{name}} occupancy', { name: nameOf(s) })}
          className="max-w-40"
        />
      ),
    },
    ...(canManage
      ? [
          {
            key: 'actions',
            header: <span className="sr-only">{t('Actions')}</span>,
            align: 'right' as const,
            cell: actions,
          },
        ]
      : []),
  ]

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted">
          {wholeClass
            ? t('This class is taught as one group. Name its section before adding another one.')
            : t('Students are enrolled into a section. Capacity limits new admissions.')}
        </p>
        <Can permission="academic.manage">
          <Button
            variant="soft"
            size="sm"
            leftIcon={<Plus />}
            disabled={wholeClass}
            onClick={() => setEditing('new')}
          >
            {t('Add section')}
          </Button>
        </Can>
      </div>
      <div className="overflow-hidden rounded-card border border-line">
        <DataTable
          caption={t('{{name}} sections', { name: schoolClass.name })}
          columns={columns}
          rows={schoolClass.sections}
          getRowId={(s) => s.id}
          mobileCard={(s) => (
            <div className="flex items-start gap-3">
              <span
                className="grid size-10 shrink-0 place-items-center rounded-control bg-brand-50 font-semibold text-brand-700"
                title={s.name ? undefined : t('Whole class')}
              >
                {s.name || <LayoutGrid className="size-4" aria-hidden="true" />}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">
                  {s.classTeacher?.name ?? t('No class teacher')}
                </p>
                <p className="truncate text-xs text-muted">
                  {[s.shift?.name, s.room].filter(Boolean).join(' · ') || t('No shift or room')}
                </p>
                <Occupancy
                  count={s.studentCount}
                  capacity={s.capacity}
                  label={t('Section {{name}} occupancy', { name: nameOf(s) })}
                  className="mt-2"
                />
              </div>
              {canManage ? actions(s) : null}
            </div>
          )}
          empty={
            <EmptyState
              icon={<LayoutGrid />}
              title={t('No sections yet')}
              description={t('Add a section so students can be enrolled in this class.')}
              compact
            />
          }
        />
      </div>

      <SectionDialog
        open={editing !== null}
        onOpenChange={(open) => (open ? undefined : setEditing(null))}
        classId={schoolClass.id}
        className={schoolClass.name}
        section={editing === 'new' ? null : editing}
        suggestedName={nextSectionName(schoolClass.sections)}
        canBeUnnamed={otherSections.length === 0}
      />
      <DeleteDialog
        item={deleting}
        onClose={() => setDeleting(null)}
        title={(s) => t('Delete section {{name}}?', { name: nameOf(s) })}
        description={(s) =>
          s.studentCount > 0
            ? t('{{count}} students are enrolled in this section. Move them to another section first.', {
                count: s.studentCount,
              })
            : t('This cannot be undone.')
        }
        onDelete={(s) => remove.mutateAsync({ classId: schoolClass.id, id: s.id })}
        successMessage={(s) => t('Section {{name}} deleted', { name: nameOf(s) })}
      />
    </>
  )
}
